import { openComponentInPopup, QueryParameter } from "../common";
import LoadingIndication from "../loading-indication";
import { PopupOptions, PopupWidth } from "../popup";

// The single TronDesigner chooser shown before entering the editor from product detail (Addendum 2 §1,
// with §1.2 and §1.3 merged into one dialog): "Design & order" vs "Download design preview PDF".
// Rides the ConfirmDialog view-name mechanism (Views/Shared/Components/ConfirmDialog/TronDesignerStart.cshtml).
//
// The preview card is dynamic: when a current-session independent design exists for the product
// (hasExistingDesign), the dialog gets the .td-start-dialog--has-design modifier — the title switches to
// "continue", the preview card stops being one click target, and its "Edit previous design" /
// "Start a new design" sub-buttons take over. The order path stays visible either way (a customer who
// made a preview and then decides to buy must not lose it).
//
// Handlers may return a Promise. While it is pending the dialog stays open with a loading overlay —
// opening the TronDesigner SDK takes seconds, and closing the dialog immediately would leave the
// customer staring at a page where seemingly nothing happens. Resolve the promise when the designer
// is actually ready (e.g. after awaiting getTronDesignerAsync()); the dialog then closes itself.
// On rejection the overlay is removed and the dialog stays usable so the customer can retry or cancel.
export interface TronDesignerStartDialogHandlers {
    // "Design & order": calculate options -> design -> add to cart (the TCC/calculator flow).
    onDesignAndOrder?: () => void | Promise<void>;
    // A NEW independent preview design: the whole preview card without an existing design, or the
    // "Start a new design" sub-button with one.
    onPreviewPdf?: () => void | Promise<void>;
    // Reopen the session's previous independent design ("Edit previous design" sub-button;
    // only reachable with hasExistingDesign).
    onEditPrevious?: () => void | Promise<void>;
    onCancel?: () => void;
}

export interface TronDesignerStartDialogOptions {
    // A current-session independent design exists for this product — show the edit/new sub-choice.
    hasExistingDesign?: boolean;
    // Whether a TCC/price ("Design & order") flow is available for this product. False on no-price /
    // enquiry-only and out-of-stock-blocked products (no #enter-calculator) — the order card is then
    // hidden so its click can't be a dead action; only the preview/proofreading path remains.
    hasOrderFlow?: boolean;
}

export async function openTronDesignerStartDialog(
    handlers: TronDesignerStartDialogHandlers = {},
    options: TronDesignerStartDialogOptions = {}
): Promise<void> {
    const queryParams: QueryParameter[] = [{
        key: "confirmDialogViewName",
        value: "TronDesignerStart"
    }];

    const popup = await openComponentInPopup("ConfirmDialog", queryParams, <PopupOptions>{
        id: "td-start-dialog-popup",
        width: PopupWidth.Medium
    });

    const root = document.querySelector<HTMLElement>("[data-component=ConfirmDialog].td-start-dialog");
    if (!root) {
        return;
    }

    if (options.hasExistingDesign) {
        root.classList.add("td-start-dialog--has-design");
    }

    // No TCC/price flow for this product — hide the "Design & order" card (leaving only the preview path)
    // so it can't be clicked into a dead action. Its handler is left unbound below.
    const orderCardHidden = options.hasOrderFlow === false;
    if (orderCardHidden) {
        const orderCard = root.querySelector<HTMLElement>('[data-option="design-order"]');
        if (orderCard) {
            orderCard.style.display = "none";
        }
    }

    // One choice per dialog: while a handler runs, further clicks (all options, Cancel) are ignored.
    let busy = false;

    const bindOption = (option: string, handler?: () => void | Promise<void>): void => {
        const element = root.querySelector<HTMLElement>(`[data-option="${option}"]`);
        if (!element) {
            return;
        }

        const activate = (evt: Event): void => {
            evt.preventDefault();
            if (busy) {
                return;
            }

            if (!handler) {
                popup.close();
                return;
            }

            const result = handler();
            if (!(result instanceof Promise)) {
                popup.close();
                return;
            }

            busy = true;
            LoadingIndication.show(root);
            result
                .then(() => popup.close())
                .catch(err => {
                    console.error("TronDesigner start dialog: option handler failed —", err);
                    LoadingIndication.hide(root);
                    busy = false;
                });
        };

        element.addEventListener("click", activate);
        // The option cards are divs with role="button" (real buttons cannot nest the sub-buttons) —
        // give them native-like keyboard activation. Real <button>s already synthesize click.
        if (element.tagName !== "BUTTON") {
            element.addEventListener("keydown", (evt: KeyboardEvent) => {
                if (evt.key === "Enter" || evt.key === " ") {
                    activate(evt);
                }
            });
        }
    };

    if (!orderCardHidden) {
        bindOption("design-order", handlers.onDesignAndOrder);
    }

    if (options.hasExistingDesign) {
        // The card itself is inert (CSS drops its pointer affordance); only the sub-buttons act.
        bindOption("preview-edit", handlers.onEditPrevious);
        bindOption("preview-new", handlers.onPreviewPdf);
    } else {
        bindOption("preview-pdf", handlers.onPreviewPdf);
    }

    const cancel = root.querySelector<HTMLElement>('[data-option="cancel"]');
    const cancelActivate = (evt: Event): void => {
        evt.preventDefault();
        if (busy) {
            return;
        }
        popup.close();
        handlers.onCancel?.();
    };
    cancel?.addEventListener("click", cancelActivate);
    cancel?.addEventListener("keydown", (evt: KeyboardEvent) => {
        if (evt.key === "Enter" || evt.key === " ") {
            cancelActivate(evt);
        }
    });
}
